export type RegionKey = "NZ" | "AU" | "UK" | "Other";

// Historical months (Sep 2025 → prior month): invoice-total ÷ interval_months snapshot.
// Uses total_excluding_tax (net of discounts), interval from subscription metadata,
// and considers both subscription_cycle and subscription_create invoices.
// All date arithmetic is anchored to UTC so month boundaries are deterministic
// regardless of Supabase's session timezone (Stripe stores epochs in UTC).
export const STRIPE_MRR_HISTORICAL_SQL = `
  WITH month_ends AS (
    SELECT
      TO_CHAR(gs, 'Mon') || TO_CHAR(gs, 'YY') AS label,
      (DATE_TRUNC('month', gs) + INTERVAL '1 month' - INTERVAL '1 day')::date AS d,
      ROW_NUMBER() OVER (ORDER BY gs)::int AS sort
    FROM generate_series(
      DATE '2025-09-01',
      (DATE_TRUNC('month', TIMEZONE('UTC', CURRENT_TIMESTAMP)) - INTERVAL '1 month')::date,
      INTERVAL '1 month'
    ) gs
  ),
  sub_interval AS (
    SELECT s.id AS sub_id, LOWER(s._raw_data->>'currency') AS ccy,
      -- Region with IE-style override: any non-AU/NZ/GB/UK customer counts
      -- as Other regardless of billing currency.
      CASE
        WHEN c._raw_data->'address'->>'country' IS NOT NULL
          AND c._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
        THEN 'Other'
        WHEN UPPER(s._raw_data->>'currency') = 'NZD' THEN 'NZ'
        WHEN UPPER(s._raw_data->>'currency') = 'AUD' THEN 'AU'
        WHEN UPPER(s._raw_data->>'currency') = 'GBP' THEN 'UK'
        ELSE 'Other'
      END AS region,
      CASE s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval'
        WHEN 'month' THEN COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1)::numeric
        WHEN 'year'  THEN (12 * COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1))::numeric
        ELSE 1::numeric
      END AS months_in_interval
    FROM stripe.subscriptions s
    LEFT JOIN stripe.customers c ON c.id = s.customer
    WHERE s.livemode
  ),
  inv_base AS (
    SELECT
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS total_net,
      TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->'lines'->'data'->0->'period'->>'start')::bigint))::date AS p_start,
      TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->'lines'->'data'->0->'period'->>'end')::bigint))::date AS p_end,
      (i._raw_data->>'created')::bigint AS created_unix,
      CASE (i._raw_data->>'billing_reason')
        WHEN 'subscription_cycle'  THEN 1
        WHEN 'subscription_create' THEN 2
      END AS br_rank
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') IN ('subscription_cycle','subscription_create')
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
  )
  SELECT me.label, me.sort, UPPER(si.ccy) AS ccy, si.region,
    ROUND(SUM(ii.total_net / NULLIF(si.months_in_interval, 0))::numeric, 0) AS mrr_local
  FROM month_ends me
  CROSS JOIN LATERAL (
    SELECT DISTINCT ON (sub_id) sub_id, total_net
    FROM inv_base
    WHERE p_start <= me.d AND p_end > me.d
    ORDER BY sub_id, br_rank ASC, created_unix DESC
  ) ii
  JOIN sub_interval si ON si.sub_id = ii.sub_id
  WHERE UPPER(si.ccy) IN ('AUD','GBP','NZD','USD')
  GROUP BY me.label, me.sort, UPPER(si.ccy), si.region
  ORDER BY me.sort, ccy, si.region
`;

// Current month: hybrid methodology differentiating three subscription states:
//   STABLE  (cycle invoice, no updates since) → cycle_total ÷ interval_months
//   NEW     (create invoice only)             → create_total ÷ interval_months
//   EXPANSION/CONTRACTION (cycle + updates)   → current_gross × keep_rate ÷ interval_months
// keep_rate is parsed from most recent subscription_update line description ("with X% off").
export const STRIPE_MRR_CURRENT_SQL = `
  WITH sub_interval AS (
    SELECT s.id AS sub_id, s.customer, LOWER(s._raw_data->>'currency') AS ccy, s.status,
      CASE
        WHEN c._raw_data->'address'->>'country' IS NOT NULL
          AND c._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
        THEN 'Other'
        WHEN UPPER(s._raw_data->>'currency') = 'NZD' THEN 'NZ'
        WHEN UPPER(s._raw_data->>'currency') = 'AUD' THEN 'AU'
        WHEN UPPER(s._raw_data->>'currency') = 'GBP' THEN 'UK'
        ELSE 'Other'
      END AS region,
      CASE s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval'
        WHEN 'month' THEN COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1)::numeric
        WHEN 'year'  THEN (12 * COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1))::numeric
        ELSE 1::numeric
      END AS months_in_interval
    FROM stripe.subscriptions s
    LEFT JOIN stripe.customers c ON c.id = s.customer
    WHERE s.livemode AND s.status IN ('active','past_due')
  ),
  current_gross AS (
    SELECT s.id AS sub_id,
      SUM((item->'price'->>'unit_amount')::numeric/100 * COALESCE((item->>'quantity')::numeric,1)) AS gross
    FROM stripe.subscriptions s
    CROSS JOIN LATERAL jsonb_array_elements(s._raw_data->'items'->'data') item
    WHERE s.livemode AND s.status IN ('active','past_due')
    GROUP BY s.id
  ),
  latest_cycle AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS cycle_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_cycle,
      (i._raw_data->>'created')::bigint AS cycle_created_unix
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_cycle'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  latest_create AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS create_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_create
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_create'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  expansion_rate AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      CASE
        WHEN (regexp_match(line->>'description', 'with (\\d+(?:\\.\\d+)?)% off'))[1] IS NOT NULL
          THEN 1.0 - ((regexp_match(line->>'description', 'with (\\d+(?:\\.\\d+)?)% off'))[1]::numeric / 100.0)
        ELSE NULL
      END AS keep_rate_update
    FROM stripe.invoices i
    CROSS JOIN LATERAL jsonb_array_elements(i._raw_data->'lines'->'data') line
    WHERE i.status='paid' AND i.livemode
      AND i._raw_data->>'billing_reason' = 'subscription_update'
      AND (line->>'amount')::numeric > 0
      AND line->>'description' LIKE '%off%'
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  expansion_subs AS (
    SELECT DISTINCT i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id
    FROM stripe.invoices i
    JOIN latest_cycle lc
      ON lc.sub_id = i._raw_data->'parent'->'subscription_details'->>'subscription'
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_update'
      AND (i._raw_data->>'created')::bigint > lc.cycle_created_unix
  )
  SELECT UPPER(si.ccy) AS ccy, si.region,
    ROUND(SUM(
      CASE
        WHEN es.sub_id IS NOT NULL
          THEN (cg.gross * COALESCE(er.keep_rate_update, lc.keep_rate_cycle, 1.0)) / NULLIF(si.months_in_interval, 0)
        WHEN lc.sub_id IS NOT NULL
          THEN lc.cycle_total / NULLIF(si.months_in_interval, 0)
        WHEN lcr.sub_id IS NOT NULL
          -- NEW bucket: use current_gross x keep_rate_create instead of stale create_total.
          -- Captures mid-cycle item upgrades (Plus -> Pro etc.) before the next cycle invoice fires.
          -- For unchanged subs this equals create_total (gross x keep_rate = total).
          THEN (cg.gross * COALESCE(lcr.keep_rate_create, 1.0)) / NULLIF(si.months_in_interval, 0)
        ELSE 0
      END
    )::numeric, 0) AS mrr_local
  FROM sub_interval si
  JOIN current_gross cg ON cg.sub_id = si.sub_id
  LEFT JOIN latest_cycle lc ON lc.sub_id = si.sub_id
  LEFT JOIN latest_create lcr ON lcr.sub_id = si.sub_id
  LEFT JOIN expansion_rate er ON er.sub_id = si.sub_id
  LEFT JOIN expansion_subs es ON es.sub_id = si.sub_id
  WHERE UPPER(si.ccy) IN ('AUD','GBP','NZD','USD')
  GROUP BY UPPER(si.ccy), si.region
  ORDER BY ccy, si.region
`;

// Product split: hybrid methodology at subscription_item level, matching headline MRR exactly.
// Expansion subs use current commitment × keep_rate; stable use cycle invoice total allocated
// by line proportion; new use create invoice total allocated by line proportion.
export const PRODUCT_SPLIT_SQL = `
  WITH sub_items_flat AS (
    SELECT s.id AS sub_id, s.customer, LOWER(s._raw_data->>'currency') AS ccy,
      CASE
        WHEN c._raw_data->'address'->>'country' IS NOT NULL
          AND c._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
        THEN 'Other'
        WHEN UPPER(s._raw_data->>'currency') = 'NZD' THEN 'NZ'
        WHEN UPPER(s._raw_data->>'currency') = 'AUD' THEN 'AU'
        WHEN UPPER(s._raw_data->>'currency') = 'GBP' THEN 'UK'
        ELSE 'Other'
      END AS region,
      item->'price'->>'product' AS product_id,
      (item->'price'->>'unit_amount')::numeric/100 AS unit,
      COALESCE((item->>'quantity')::numeric, 1) AS qty,
      CASE item->'price'->'recurring'->>'interval'
        WHEN 'month' THEN COALESCE((item->'price'->'recurring'->>'interval_count')::int,1)::numeric
        WHEN 'year'  THEN (12 * COALESCE((item->'price'->'recurring'->>'interval_count')::int,1))::numeric
        ELSE 1::numeric
      END AS months_in_interval
    FROM stripe.subscriptions s
    LEFT JOIN stripe.customers c ON c.id = s.customer
    CROSS JOIN LATERAL jsonb_array_elements(s._raw_data->'items'->'data') item
    WHERE s.livemode AND s.status IN ('active','past_due')
  ),
  sub_totals AS (
    SELECT sub_id, SUM(unit * qty) AS sub_gross
    FROM sub_items_flat GROUP BY sub_id
  ),
  latest_cycle AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS cycle_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_cycle,
      (i._raw_data->>'created')::bigint AS cycle_created_unix
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_cycle'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  latest_create AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS create_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_create
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_create'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  expansion_rate AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      CASE
        WHEN (regexp_match(line->>'description', 'with (\\d+(?:\\.\\d+)?)% off'))[1] IS NOT NULL
          THEN 1.0 - ((regexp_match(line->>'description', 'with (\\d+(?:\\.\\d+)?)% off'))[1]::numeric / 100.0)
        ELSE NULL
      END AS keep_rate_update
    FROM stripe.invoices i
    CROSS JOIN LATERAL jsonb_array_elements(i._raw_data->'lines'->'data') line
    WHERE i.status='paid' AND i.livemode
      AND i._raw_data->>'billing_reason' = 'subscription_update'
      AND (line->>'amount')::numeric > 0
      AND line->>'description' LIKE '%off%'
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  expansion_subs AS (
    SELECT DISTINCT i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id
    FROM stripe.invoices i
    JOIN latest_cycle lc ON lc.sub_id = i._raw_data->'parent'->'subscription_details'->>'subscription'
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_update'
      AND (i._raw_data->>'created')::bigint > lc.cycle_created_unix
  )
  SELECT UPPER(sif.ccy) AS ccy, sif.region,
    CASE sif.product_id
      WHEN 'prod_RtGkPom4rQa0Ib' THEN 'Plus'
      WHEN 'prod_Tp5mwudvPWE3bH' THEN 'Pro'
      ELSE 'Other'
    END AS product,
    COUNT(DISTINCT sif.customer) AS firms,
    SUM(sif.qty)::int AS seats,
    ROUND(SUM(
      CASE
        WHEN es.sub_id IS NOT NULL
          THEN sif.unit * sif.qty * COALESCE(er.keep_rate_update, lc.keep_rate_cycle, 1.0) / NULLIF(sif.months_in_interval, 0)
        WHEN lc.sub_id IS NOT NULL
          THEN (lc.cycle_total * sif.unit * sif.qty / NULLIF(st.sub_gross, 0)) / NULLIF(sif.months_in_interval, 0)
        WHEN lcr.sub_id IS NOT NULL
          THEN (lcr.create_total * sif.unit * sif.qty / NULLIF(st.sub_gross, 0)) / NULLIF(sif.months_in_interval, 0)
        ELSE 0
      END
    )::numeric, 0) AS mrr_local
  FROM sub_items_flat sif
  JOIN sub_totals st ON st.sub_id = sif.sub_id
  LEFT JOIN latest_cycle lc ON lc.sub_id = sif.sub_id
  LEFT JOIN latest_create lcr ON lcr.sub_id = sif.sub_id
  LEFT JOIN expansion_rate er ON er.sub_id = sif.sub_id
  LEFT JOIN expansion_subs es ON es.sub_id = sif.sub_id
  WHERE UPPER(sif.ccy) IN ('AUD','GBP','NZD','USD')
  GROUP BY UPPER(sif.ccy), sif.region, sif.product_id
`;

// (Option A prior-month MRR is derived in TypeScript from STRIPE_PER_SUB_DELTA_SQL —
// sum current_mrr where prior_mrr > 0 — see MrrDashboard/index.tsx for the aggregation.)

// Active-user headcount from the application DB (NOT stripe schema). Aggregates
// user_roles joined to billing plan + company country, returning seats (distinct
// user_roles rows) and firms (distinct companies) per (plan, country). This is
// the source of truth for headcount; revenue comes from Stripe.
export const ACTIVE_USERS_SQL = `
  SELECT
    cb.billing_plan,
    c.country_code,
    COUNT(*) AS seat_count,
    COUNT(DISTINCT c.external_id) AS firm_count
  FROM user_roles ur
  JOIN company_billing cb
    ON ur.company_external_id = cb.company_external_id
  JOIN companies c
    ON ur.company_external_id = c.external_id
  GROUP BY cb.billing_plan, c.country_code
  ORDER BY cb.billing_plan, c.country_code
`;

export type ActiveUserRow = {
  billing_plan: string;
  country_code: string;
  seat_count: number;
  firm_count: number;
};

export function mapActiveUserRows(columns: string[], rows: unknown[][]): ActiveUserRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    billing_plan: String(get(r, "billing_plan") ?? ""),
    country_code: String(get(r, "country_code") ?? ""),
    seat_count: Number(get(r, "seat_count") ?? 0),
    firm_count: Number(get(r, "firm_count") ?? 0),
  }));
}

// "New paying seats" added in a given month, by country. Builds the SQL with the
// month-start date inlined as a date literal because the /admin/query endpoint
// does not currently accept parameter binding cleanly for this shape (every other
// useAdminQuery in the repo uses []). The input is strictly validated as
// YYYY-MM-DD so there is no injection risk.
//
// Two sources combined:
//   1. New firms — every seat on a firm whose subscription started this month.
//   2. New seats on existing firms — seats whose user signed up this month
//      (users.created_at_utc) on a firm that was already paying before this month.
// Caveat: user_roles has no creation timestamp, so we proxy seat-add time with
// the user's signup time. Pre-existing users newly assigned to a paying firm
// this month would not be counted by this proxy.
export function buildNewSeatsByCountrySql(monthStartIso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(monthStartIso)) {
    throw new Error(`buildNewSeatsByCountrySql: invalid date '${monthStartIso}'`);
  }
  return `
    SELECT c.country_code, cb.billing_plan, COUNT(*) AS new_seats
    FROM user_roles ur
    JOIN companies c ON c.external_id = ur.company_external_id
    JOIN company_billing cb ON cb.company_external_id = ur.company_external_id
    JOIN users u ON u.external_id = ur.user_external_id
    WHERE ur.status = 'active'
      AND cb.billing_plan IN ('pro','plus')
      AND ur.role IN ('adviser','admin','manager')
      AND (
        (cb.subscription_started_date_utc >= DATE '${monthStartIso}'
         AND cb.subscription_started_date_utc < DATE '${monthStartIso}' + INTERVAL '1 month')
        OR ((cb.subscription_started_date_utc < DATE '${monthStartIso}'
              OR cb.subscription_started_date_utc IS NULL)
            AND u.created_at_utc >= DATE '${monthStartIso}'
            AND u.created_at_utc < DATE '${monthStartIso}' + INTERVAL '1 month')
      )
    GROUP BY c.country_code, cb.billing_plan
    ORDER BY c.country_code, cb.billing_plan
  `;
}

// Net seat change per (currency, plan) for the month beginning at monthStartIso,
// derived from Stripe sub state. For each sub:
//   - if created in the month: net = current quantity (all seats are new)
//   - if ended in the month:   net = -prior quantity (lost those seats)
//   - else (continuing):       net = current quantity - prior quantity
// Prior quantity = the quantity at the most recent paid subscription_cycle
// invoice with created < monthStartIso, falling back to subscription_create
// when the sub hasn't cycled yet (still in its first billing period).
// Caveat: Stripe doesn't know app-DB roles, so this counts every billed seat
// regardless of role (adviser, admin, manager, support).
export function buildNetSeatsByCcySql(monthStartIso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(monthStartIso)) {
    throw new Error(`buildNetSeatsByCcySql: invalid date '${monthStartIso}'`);
  }
  return `
    WITH sms AS (SELECT DATE '${monthStartIso}' AS d),
    cur_subs AS (
      SELECT
        s.id AS sub_id,
        -- Route customers whose billing-address country isn't AU/NZ/GB/UK to
        -- 'OTH' (Other) regardless of their actual currency. Catches IE
        -- customers paying in GBP who would otherwise count as UK seats.
        CASE
          WHEN c._raw_data->'address'->>'country' IS NOT NULL
            AND c._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
          THEN 'OTH'
          ELSE UPPER(s._raw_data->>'currency')
        END AS ccy,
        (s._raw_data->>'created')::bigint AS sub_created_unix,
        NULLIF(s._raw_data->>'ended_at', '')::bigint AS sub_ended_unix,
        (
          SELECT SUM(COALESCE((it->>'quantity')::int, 1))
          FROM jsonb_array_elements(s._raw_data->'items'->'data') it
        )::int AS current_qty,
        CASE (
          SELECT inner_item->'price'->>'product'
          FROM jsonb_array_elements(s._raw_data->'items'->'data') inner_item
          ORDER BY (inner_item->'price'->>'unit_amount')::numeric * COALESCE((inner_item->>'quantity')::numeric,1) DESC
          LIMIT 1
        )
          WHEN 'prod_RtGkPom4rQa0Ib' THEN 'plus'
          WHEN 'prod_Tp5mwudvPWE3bH' THEN 'pro'
          ELSE 'other'
        END AS plan
      FROM stripe.subscriptions s
      LEFT JOIN stripe.customers c ON c.id = s.customer
      WHERE s.livemode
    ),
    -- Prior quantity = qty at the most recent paid invoice before the month.
    -- Includes subscription_update so late-prior-month seat changes don't get
    -- mis-attributed to the selected month (Remaining time on N x Plan is the
    -- positive line that captures the post-update quantity).
    prior AS (
      SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
        i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
        (
          SELECT SUM(COALESCE((line->>'quantity')::int, 1))
          FROM jsonb_array_elements(i._raw_data->'lines'->'data') line
          WHERE (line->>'amount')::numeric > 0
        )::int AS prior_qty
      FROM stripe.invoices i, sms
      WHERE i.livemode
        AND i.status = 'paid'
        AND i._raw_data->>'billing_reason' IN ('subscription_cycle', 'subscription_create', 'subscription_update')
        AND TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->>'created')::bigint))::date < sms.d
      ORDER BY
        i._raw_data->'parent'->'subscription_details'->>'subscription',
        (i._raw_data->>'created')::bigint DESC
    )
    SELECT
      cs.ccy,
      cs.plan,
      SUM(
        CASE
          WHEN cs.sub_created_unix >= EXTRACT(epoch FROM (SELECT d FROM sms))::bigint
            THEN cs.current_qty
          WHEN cs.sub_ended_unix IS NOT NULL
            AND cs.sub_ended_unix >= EXTRACT(epoch FROM (SELECT d FROM sms))::bigint
            AND cs.sub_ended_unix < EXTRACT(epoch FROM (SELECT d FROM sms) + INTERVAL '1 month')::bigint
            THEN -COALESCE(p.prior_qty, 0)
          ELSE cs.current_qty - COALESCE(p.prior_qty, 0)
        END
      )::int AS net_seats
    FROM cur_subs cs
    LEFT JOIN prior p ON p.sub_id = cs.sub_id
    WHERE cs.plan IN ('pro', 'plus')
      AND (cs.sub_ended_unix IS NULL OR cs.sub_ended_unix >= EXTRACT(epoch FROM (SELECT d FROM sms))::bigint)
    GROUP BY cs.ccy, cs.plan
    ORDER BY cs.ccy, cs.plan
  `;
}

// Paying-seat count per (month, currency, plan) for each month from Sep 2025
// through the current month. End-of-month state derived from the latest paid
// subscription_cycle / _create / _update invoice for each sub at or before the
// month-end date. Subs active during the month (created on/before, not yet
// ended) contribute. Used to build a stacked time series of paying seats by
// region/plan on the Growth Dashboard Detail tab.
export const SEAT_HISTORY_BY_CCY_PLAN_SQL = `
  WITH months AS (
    SELECT
      (DATE_TRUNC('month', gs) + INTERVAL '1 month' - INTERVAL '1 day')::date AS month_end,
      TO_CHAR(gs, 'YYYY-MM') AS ym
    FROM generate_series(
      DATE '2025-09-01',
      DATE_TRUNC('month', CURRENT_DATE),
      INTERVAL '1 month'
    ) gs
  ),
  subs_base AS (
    SELECT
      s.id AS sub_id,
      -- Override ccy to 'OTH' for customers outside AU/NZ/GB/UK so e.g. IE
      -- subs paying in GBP route to "Other" instead of UK.
      CASE
        WHEN c._raw_data->'address'->>'country' IS NOT NULL
          AND c._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
        THEN 'OTH'
        ELSE UPPER(s._raw_data->>'currency')
      END AS ccy,
      TIMEZONE('UTC', TO_TIMESTAMP((s._raw_data->>'created')::bigint))::date AS sub_created_date,
      CASE WHEN NULLIF(s._raw_data->>'ended_at', '') IS NULL THEN NULL
        ELSE TIMEZONE('UTC', TO_TIMESTAMP((s._raw_data->>'ended_at')::bigint))::date END AS sub_ended_date,
      CASE (
        SELECT inner_item->'price'->>'product'
        FROM jsonb_array_elements(s._raw_data->'items'->'data') inner_item
        ORDER BY (inner_item->'price'->>'unit_amount')::numeric * COALESCE((inner_item->>'quantity')::numeric,1) DESC
        LIMIT 1
      )
        WHEN 'prod_RtGkPom4rQa0Ib' THEN 'plus'
        WHEN 'prod_Tp5mwudvPWE3bH' THEN 'pro'
        ELSE 'other'
      END AS plan
    FROM stripe.subscriptions s
    LEFT JOIN stripe.customers c ON c.id = s.customer
    WHERE s.livemode
  ),
  latest_inv_per_sub_per_month AS (
    SELECT DISTINCT ON (sb.sub_id, m.ym)
      sb.sub_id, sb.ccy, sb.plan, m.ym,
      (
        SELECT SUM(COALESCE((line->>'quantity')::int, 1))::int
        FROM jsonb_array_elements(i._raw_data->'lines'->'data') line
        WHERE (line->>'amount')::numeric > 0
      ) AS qty
    FROM subs_base sb
    CROSS JOIN months m
    JOIN stripe.invoices i ON i._raw_data->'parent'->'subscription_details'->>'subscription' = sb.sub_id
    WHERE i.livemode
      AND i.status = 'paid'
      AND i._raw_data->>'billing_reason' IN ('subscription_cycle', 'subscription_create', 'subscription_update')
      AND TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->>'created')::bigint))::date <= m.month_end
      AND sb.sub_created_date <= m.month_end
      AND (sb.sub_ended_date IS NULL OR sb.sub_ended_date > m.month_end)
      AND sb.plan IN ('pro', 'plus')
    ORDER BY sb.sub_id, m.ym, (i._raw_data->>'created')::bigint DESC
  )
  SELECT ym, ccy, plan, SUM(qty)::int AS seats
  FROM latest_inv_per_sub_per_month
  WHERE qty IS NOT NULL
  GROUP BY ym, ccy, plan
  ORDER BY ym, ccy, plan
`;

// Per-month AAM seat counts split by (country_code, plan) including Free.
// Used by the Detail tab to render small-multiple line charts per country.
//
// Methodology (app DB, no Stripe involvement):
// - plan_at_month: for each (company, month_end), the most-recent
//   company_billing row whose subscription_started_date_utc <= month_end gives
//   the effective plan. Companies/months with no matching row default to 'free'
//   (the implicit pre-paid state).
// - Seat count: active AAM user_roles (status='active', admin/adviser/manager)
//   whose user.created_at_utc <= month_end. user_roles has no own timestamp so
//   we proxy "joined-by" with users.created_at_utc. This undercounts historical
//   headcount for users who have since been removed (since user_roles only
//   keeps current rows), but trend is preserved.
export const SEAT_HISTORY_BY_COUNTRY_PLAN_SQL = `
  WITH months AS (
    SELECT
      (DATE_TRUNC('month', gs) + INTERVAL '1 month' - INTERVAL '1 day')::date AS month_end,
      TO_CHAR(gs, 'YYYY-MM') AS ym
    FROM generate_series(
      DATE '2025-09-01',
      DATE_TRUNC('month', CURRENT_DATE),
      INTERVAL '1 month'
    ) gs
  ),
  plan_at_month AS (
    SELECT DISTINCT ON (cb.company_external_id, m.ym)
      cb.company_external_id,
      m.ym,
      m.month_end,
      cb.billing_plan::text AS plan
    FROM public.company_billing cb
    CROSS JOIN months m
    WHERE cb.subscription_started_date_utc IS NULL
       OR cb.subscription_started_date_utc::date <= m.month_end
    ORDER BY cb.company_external_id, m.ym, cb.subscription_started_date_utc DESC NULLS LAST
  )
  SELECT
    m.ym,
    c.country_code,
    COALESCE(pam.plan, 'free') AS plan,
    COUNT(*)::int AS seats
  FROM months m
  CROSS JOIN public.companies c
  LEFT JOIN plan_at_month pam
    ON pam.company_external_id = c.external_id
   AND pam.ym = m.ym
  JOIN public.user_roles ur
    ON ur.company_external_id = c.external_id
   AND ur.status = 'active'
   AND ur.role::text IN ('admin','adviser','manager')
  JOIN public.users u
    ON u.external_id = ur.user_external_id
   AND u.created_at_utc::date <= m.month_end
  WHERE c.country_code IS NOT NULL
    AND COALESCE(pam.plan, 'free') IN ('free','pro','plus')
  GROUP BY m.ym, c.country_code, COALESCE(pam.plan, 'free')
  ORDER BY m.ym, c.country_code, COALESCE(pam.plan, 'free')
`;

export type SeatHistoryByCountryPlanRow = {
  ym: string;
  country_code: string;
  plan: "free" | "pro" | "plus";
  seats: number;
};

export function mapSeatHistoryByCountryPlanRows(
  columns: string[],
  rows: unknown[][],
): SeatHistoryByCountryPlanRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  const ALLOWED = new Set<SeatHistoryByCountryPlanRow["plan"]>(["free","pro","plus"]);
  return rows.flatMap((r) => {
    const plan = String(get(r, "plan") ?? "");
    if (!ALLOWED.has(plan as SeatHistoryByCountryPlanRow["plan"])) return [];
    return [{
      ym: String(get(r, "ym") ?? ""),
      country_code: String(get(r, "country_code") ?? ""),
      plan: plan as SeatHistoryByCountryPlanRow["plan"],
      seats: Number(get(r, "seats") ?? 0),
    }];
  });
}

// Invoiced (non-Stripe) paying firms with their current AAM seat counts and
// subscription start dates. Used to overlay the Stripe time series — invoiced
// firms aren't in stripe.subscriptions but their seats absolutely count toward
// paid-seat totals. Each firm contributes a flat aam_seats count to every
// month from its subscription_started_date_utc onward.
export const INVOICED_FIRMS_SEATS_SQL = `
  SELECT
    c.country_code,
    cb.billing_plan::text AS plan,
    cb.subscription_started_date_utc::date AS start_date,
    c.external_id::text AS company_id,
    (
      SELECT COUNT(*)
      FROM public.user_roles ur
      WHERE ur.company_external_id = c.external_id
        AND ur.status = 'active'
        AND ur.role::text IN ('admin','adviser','manager')
    )::int AS aam_seats
  FROM public.company_billing cb
  JOIN public.companies c ON c.external_id = cb.company_external_id
  WHERE cb.stripe_customer_id IS NULL
    AND cb.billing_plan::text IN ('pro','plus')
`;

export type InvoicedFirmRow = {
  country_code: string;
  plan: string;
  start_date: string | null;
  company_id: string;
  aam_seats: number;
};

export function mapInvoicedFirmRows(columns: string[], rows: unknown[][]): InvoicedFirmRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    country_code: String(get(r, "country_code") ?? ""),
    plan: String(get(r, "plan") ?? ""),
    start_date: (get(r, "start_date") as string | null) ?? null,
    company_id: String(get(r, "company_id") ?? ""),
    aam_seats: Number(get(r, "aam_seats") ?? 0),
  }));
}

// Slim cohort view: one row per (active user, company) with signup date,
// country, and conversion status. Drives the seat forecast on the Growth
// Dashboard Detail tab — both the historical conversion curve (for signups
// 12+ months old who have had time to convert) and the current pool of
// unconverted accounts.
export const SIGNUP_COHORT_SQL = `
  WITH paid_billing AS (
    SELECT
      cb.company_external_id,
      bool_or(cb.billing_plan::text IN ('pro','plus')) AS on_paid_plan,
      MIN(cb.subscription_started_date_utc) FILTER (
        WHERE cb.billing_plan::text IN ('pro','plus')
      ) AS first_paid_date,
      (array_agg(cb.billing_plan::text
          ORDER BY cb.subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan
    FROM public.company_billing cb
    GROUP BY cb.company_external_id
  )
  SELECT
    ur.user_external_id::text   AS user_id,
    ur.company_external_id::text AS company_id,
    u.created_at_utc            AS signup_date,
    cmp.country_code,
    COALESCE(b.on_paid_plan, false) AS on_paid_plan,
    b.first_paid_date           AS converted_date,
    b.billing_plan
  FROM public.user_roles ur
  JOIN public.users u ON u.external_id = ur.user_external_id
  JOIN public.companies cmp ON cmp.external_id = ur.company_external_id
  LEFT JOIN paid_billing b ON b.company_external_id = ur.company_external_id
  WHERE ur.status = 'active'
    AND u.email NOT ILIKE '%@gomarloo.com'
    AND u.created_at_utc >= NOW() - INTERVAL '24 months'
`;

export type SignupCohortRow = {
  user_id: string;
  company_id: string;
  signup_date: string;
  country_code: string;
  on_paid_plan: boolean;
  converted_date: string | null;
  billing_plan: string | null;
};

export function mapSignupCohortRows(columns: string[], rows: unknown[][]): SignupCohortRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    user_id: String(get(r, "user_id") ?? ""),
    company_id: String(get(r, "company_id") ?? ""),
    signup_date: String(get(r, "signup_date") ?? ""),
    country_code: String(get(r, "country_code") ?? ""),
    on_paid_plan: Boolean(get(r, "on_paid_plan")),
    converted_date: (get(r, "converted_date") as string | null) ?? null,
    billing_plan: (get(r, "billing_plan") as string | null) ?? null,
  }));
}

export type SeatHistoryRow = {
  ym: string;
  ccy: string;
  plan: string;
  seats: number;
};

export function mapSeatHistoryRows(columns: string[], rows: unknown[][]): SeatHistoryRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    ym: String(get(r, "ym") ?? ""),
    ccy: String(get(r, "ccy") ?? ""),
    plan: String(get(r, "plan") ?? ""),
    seats: Number(get(r, "seats") ?? 0),
  }));
}

export type NetSeatsByCcyRow = {
  ccy: string;
  plan: string;
  net_seats: number;
};

export function mapNetSeatsByCcyRows(columns: string[], rows: unknown[][]): NetSeatsByCcyRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    ccy: String(get(r, "ccy") ?? ""),
    plan: String(get(r, "plan") ?? ""),
    net_seats: Number(get(r, "net_seats") ?? 0),
  }));
}

// Legacy: seats lost via Stripe subscription cancellations in the month beginning
// at monthStartIso. Superseded by buildNetSeatsByCcySql which gives a full net
// number including expansions and contractions on continuing subs.
export function buildLostSeatsByCcySql(monthStartIso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(monthStartIso)) {
    throw new Error(`buildLostSeatsByCcySql: invalid date '${monthStartIso}'`);
  }
  return `
    WITH ended AS (
      SELECT
        s.id AS sub_id,
        UPPER(s._raw_data->>'currency') AS ccy,
        CASE (
          SELECT inner_item->'price'->>'product'
          FROM jsonb_array_elements(s._raw_data->'items'->'data') inner_item
          ORDER BY (inner_item->'price'->>'unit_amount')::numeric * COALESCE((inner_item->>'quantity')::numeric,1) DESC
          LIMIT 1
        )
          WHEN 'prod_RtGkPom4rQa0Ib' THEN 'plus'
          WHEN 'prod_Tp5mwudvPWE3bH' THEN 'pro'
          ELSE 'other'
        END AS plan,
        (
          SELECT SUM(COALESCE((it->>'quantity')::numeric, 1))::int
          FROM jsonb_array_elements(s._raw_data->'items'->'data') it
        ) AS seats
      FROM stripe.subscriptions s
      WHERE s.livemode
        AND s.status = 'canceled'
        AND s._raw_data->>'ended_at' IS NOT NULL
        AND TIMEZONE('UTC', TO_TIMESTAMP((s._raw_data->>'ended_at')::bigint))::date >= DATE '${monthStartIso}'
        AND TIMEZONE('UTC', TO_TIMESTAMP((s._raw_data->>'ended_at')::bigint))::date < DATE '${monthStartIso}' + INTERVAL '1 month'
    )
    SELECT ccy, plan, SUM(seats)::int AS lost_seats
    FROM ended
    WHERE seats IS NOT NULL
    GROUP BY ccy, plan
    ORDER BY ccy, plan
  `;
}

export type LostSeatsRow = {
  ccy: string;
  plan: string;
  lost_seats: number;
};

export function mapLostSeatsRows(columns: string[], rows: unknown[][]): LostSeatsRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    ccy: String(get(r, "ccy") ?? ""),
    plan: String(get(r, "plan") ?? ""),
    lost_seats: Number(get(r, "lost_seats") ?? 0),
  }));
}

export type NewSeatsRow = {
  country_code: string;
  billing_plan: string;
  new_seats: number;
};

export function mapNewSeatsRows(columns: string[], rows: unknown[][]): NewSeatsRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    country_code: String(get(r, "country_code") ?? ""),
    billing_plan: String(get(r, "billing_plan") ?? ""),
    new_seats: Number(get(r, "new_seats") ?? 0),
  }));
}

// Active-user headcount with role dimension. Same join as ACTIVE_USERS_SQL but
// adds ur.role so the Detail tab can show role x plan x country breakdowns.
// status='active' filter so deactivated seats don't inflate counts; downstream
// Detail tab further filters to paying plans + AAM roles only.
export const ACTIVE_USERS_BY_ROLE_SQL = `
  SELECT
    ur.role,
    cb.billing_plan,
    c.country_code,
    COUNT(*) AS seat_count
  FROM user_roles ur
  JOIN company_billing cb
    ON ur.company_external_id = cb.company_external_id
  JOIN companies c
    ON ur.company_external_id = c.external_id
  WHERE ur.status = 'active'
  GROUP BY ur.role, cb.billing_plan, c.country_code
  ORDER BY ur.role, cb.billing_plan, c.country_code
`;

export type ActiveUserByRoleRow = {
  role: string;
  billing_plan: string;
  country_code: string;
  seat_count: number;
};

export function mapActiveUserByRoleRows(columns: string[], rows: unknown[][]): ActiveUserByRoleRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    role: String(get(r, "role") ?? ""),
    billing_plan: String(get(r, "billing_plan") ?? ""),
    country_code: String(get(r, "country_code") ?? ""),
    seat_count: Number(get(r, "seat_count") ?? 0),
  }));
}

// Diagnostic: per-sub MRR delta between prior month (historical method) and current
// month (hybrid method). Used to identify subs whose contribution shifts MoM —
// expansion / contraction / new / churned. Filterable by currency in the JS layer.
export const STRIPE_PER_SUB_DELTA_SQL = `
  WITH sub_interval AS (
    SELECT s.id AS sub_id, s.customer, LOWER(s._raw_data->>'currency') AS ccy, s.status,
      CASE s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval'
        WHEN 'month' THEN COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1)::numeric
        WHEN 'year'  THEN (12 * COALESCE((s._raw_data->'items'->'data'->0->'price'->'recurring'->>'interval_count')::int,1))::numeric
        ELSE 1::numeric
      END AS months_in_interval
    FROM stripe.subscriptions s
    WHERE s.livemode AND s.status IN ('active','past_due')
  ),
  current_gross AS (
    SELECT s.id AS sub_id,
      SUM((item->'price'->>'unit_amount')::numeric/100 * COALESCE((item->>'quantity')::numeric,1)) AS gross,
      SUM(COALESCE((item->>'quantity')::numeric, 1))::int AS seats,
      -- canonical product = the one with the largest line value (handles multi-item subs)
      (
        SELECT inner_item->'price'->>'product'
        FROM jsonb_array_elements(s._raw_data->'items'->'data') inner_item
        ORDER BY (inner_item->'price'->>'unit_amount')::numeric * COALESCE((inner_item->>'quantity')::numeric,1) DESC
        LIMIT 1
      ) AS product_id
    FROM stripe.subscriptions s
    CROSS JOIN LATERAL jsonb_array_elements(s._raw_data->'items'->'data') item
    WHERE s.livemode AND s.status IN ('active','past_due')
    GROUP BY s.id, s._raw_data
  ),
  prior_month_end AS (
    SELECT (DATE_TRUNC('month', TIMEZONE('UTC', CURRENT_TIMESTAMP)) - INTERVAL '1 day')::date AS d
  ),
  inv_base AS (
    SELECT
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS total_net,
      TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->'lines'->'data'->0->'period'->>'start')::bigint))::date AS p_start,
      TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->'lines'->'data'->0->'period'->>'end')::bigint))::date AS p_end,
      (i._raw_data->>'created')::bigint AS created_unix,
      CASE (i._raw_data->>'billing_reason')
        WHEN 'subscription_cycle'  THEN 1
        WHEN 'subscription_create' THEN 2
      END AS br_rank
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') IN ('subscription_cycle','subscription_create')
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
  ),
  prior_per_sub AS (
    SELECT DISTINCT ON (ib.sub_id) ib.sub_id, ib.total_net
    FROM inv_base ib, prior_month_end pme
    WHERE ib.p_start <= pme.d AND ib.p_end > pme.d
    ORDER BY ib.sub_id, ib.br_rank ASC, ib.created_unix DESC
  ),
  latest_cycle AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS cycle_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_cycle,
      (i._raw_data->>'created')::bigint AS cycle_created_unix
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_cycle'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  latest_create AS (
    SELECT DISTINCT ON (i._raw_data->'parent'->'subscription_details'->>'subscription')
      i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id,
      (i._raw_data->>'total_excluding_tax')::numeric / 100.0 AS create_total,
      CASE WHEN (i._raw_data->>'subtotal_excluding_tax')::numeric > 0
        THEN (i._raw_data->>'total_excluding_tax')::numeric / (i._raw_data->>'subtotal_excluding_tax')::numeric
        ELSE 1.0 END AS keep_rate_create
    FROM stripe.invoices i
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_create'
      AND i._raw_data->'parent'->'subscription_details'->>'subscription' IS NOT NULL
    ORDER BY i._raw_data->'parent'->'subscription_details'->>'subscription',
             (i._raw_data->>'created')::bigint DESC
  ),
  expansion_subs AS (
    SELECT DISTINCT i._raw_data->'parent'->'subscription_details'->>'subscription' AS sub_id
    FROM stripe.invoices i
    JOIN latest_cycle lc
      ON lc.sub_id = i._raw_data->'parent'->'subscription_details'->>'subscription'
    WHERE i.status='paid' AND i.livemode
      AND (i._raw_data->>'billing_reason') = 'subscription_update'
      AND (i._raw_data->>'created')::bigint > lc.cycle_created_unix
  )
  SELECT
    si.sub_id,
    UPPER(si.ccy) AS ccy,
    CASE
      WHEN cust._raw_data->'address'->>'country' IS NOT NULL
        AND cust._raw_data->'address'->>'country' NOT IN ('AU','NZ','GB','UK')
      THEN 'Other'
      WHEN UPPER(si.ccy) = 'NZD' THEN 'NZ'
      WHEN UPPER(si.ccy) = 'AUD' THEN 'AU'
      WHEN UPPER(si.ccy) = 'GBP' THEN 'UK'
      ELSE 'Other'
    END AS region,
    COALESCE(cust._raw_data->>'name', cust._raw_data->>'email', si.customer) AS customer,
    CASE
      WHEN es.sub_id IS NOT NULL THEN 'EXPANSION'
      WHEN lc.sub_id IS NOT NULL THEN 'STABLE'
      WHEN lcr.sub_id IS NOT NULL THEN 'NEW'
      ELSE 'NONE'
    END AS bucket,
    CASE cg.product_id
      WHEN 'prod_RtGkPom4rQa0Ib' THEN 'Plus'
      WHEN 'prod_Tp5mwudvPWE3bH' THEN 'Pro'
      ELSE 'Other'
    END AS product,
    cg.seats AS seats,
    ROUND(COALESCE(pps.total_net / NULLIF(si.months_in_interval, 0), 0)::numeric, 0) AS prior_mrr,
    ROUND((CASE
      WHEN es.sub_id IS NOT NULL
        THEN (cg.gross * COALESCE(lc.keep_rate_cycle, 1.0)) / NULLIF(si.months_in_interval, 0)
      WHEN lc.sub_id IS NOT NULL
        THEN lc.cycle_total / NULLIF(si.months_in_interval, 0)
      WHEN lcr.sub_id IS NOT NULL
        THEN (cg.gross * COALESCE(lcr.keep_rate_create, 1.0)) / NULLIF(si.months_in_interval, 0)
      ELSE 0
    END)::numeric, 0) AS current_mrr,
    ROUND((cg.gross / NULLIF(si.months_in_interval, 0))::numeric, 0) AS gross_mrr
  FROM sub_interval si
  LEFT JOIN stripe.customers cust ON cust.id = si.customer
  JOIN current_gross cg ON cg.sub_id = si.sub_id
  LEFT JOIN prior_per_sub pps ON pps.sub_id = si.sub_id
  LEFT JOIN latest_cycle lc ON lc.sub_id = si.sub_id
  LEFT JOIN latest_create lcr ON lcr.sub_id = si.sub_id
  LEFT JOIN expansion_subs es ON es.sub_id = si.sub_id
  WHERE UPPER(si.ccy) IN ('AUD','GBP','NZD','USD')
  ORDER BY UPPER(si.ccy), ABS(
    (CASE
      WHEN es.sub_id IS NOT NULL
        THEN (cg.gross * COALESCE(lc.keep_rate_cycle, 1.0)) / NULLIF(si.months_in_interval, 0)
      WHEN lc.sub_id IS NOT NULL
        THEN lc.cycle_total / NULLIF(si.months_in_interval, 0)
      WHEN lcr.sub_id IS NOT NULL
        THEN (cg.gross * COALESCE(lcr.keep_rate_create, 1.0)) / NULLIF(si.months_in_interval, 0)
      ELSE 0
    END) - COALESCE(pps.total_net / NULLIF(si.months_in_interval, 0), 0)
  ) DESC
`;

export type StripePerSubRow = {
  sub_id: string;
  ccy: string;
  region: RegionKey;
  customer: string;
  bucket: string;
  product: string;
  seats: number;
  prior_mrr: number;
  current_mrr: number;
  gross_mrr: number;
};

const REGION_KEYS = new Set<RegionKey>(["NZ", "AU", "UK", "Other"]);
function toRegion(v: unknown): RegionKey {
  const s = String(v ?? "");
  return REGION_KEYS.has(s as RegionKey) ? (s as RegionKey) : "Other";
}

export function mapPerSubRows(columns: string[], rows: unknown[][]): StripePerSubRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    sub_id: String(get(r, "sub_id")),
    ccy: String(get(r, "ccy")),
    region: toRegion(get(r, "region")),
    customer: String(get(r, "customer") ?? ""),
    bucket: String(get(r, "bucket")),
    product: String(get(r, "product") ?? "Other"),
    seats: Number(get(r, "seats") ?? 0),
    prior_mrr: Number(get(r, "prior_mrr")),
    current_mrr: Number(get(r, "current_mrr")),
    gross_mrr: Number(get(r, "gross_mrr") ?? 0),
  }));
}

// Row types and mappers

export type StripeMrrRow = {
  label: string;
  sort: number;
  ccy: string;
  region: RegionKey;
  mrr_local: number;
};

export type CurrentMrrRow = {
  ccy: string;
  region: RegionKey;
  mrr_local: number;
};

export function mapStripeMrrRows(
  columns: string[],
  rows: unknown[][],
): StripeMrrRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    label: String(get(r, "label")),
    sort: Number(get(r, "sort")),
    ccy: String(get(r, "ccy")),
    region: toRegion(get(r, "region")),
    mrr_local: Number(get(r, "mrr_local")),
  }));
}

export function mapCurrentMrrRows(
  columns: string[],
  rows: unknown[][],
): CurrentMrrRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    ccy: String(get(r, "ccy")),
    region: toRegion(get(r, "region")),
    mrr_local: Number(get(r, "mrr_local")),
  }));
}

export type ProductSplitRow = {
  ccy: string;
  region: RegionKey;
  product: string;
  firms: number;
  seats: number;
  mrr_local: number;
};

export function mapProductSplitRows(
  columns: string[],
  rows: unknown[][],
): ProductSplitRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    ccy: String(get(r, "ccy")),
    region: toRegion(get(r, "region")),
    product: String(get(r, "product")),
    firms: Number(get(r, "firms")),
    seats: Number(get(r, "seats")),
    mrr_local: Number(get(r, "mrr_local")),
  }));
}

type RegionMap = Record<RegionKey, number>;
type RegionArr = Record<RegionKey, number[]>;

export type DashboardData = {
  currentMRR: number;
  currentARR: number;
  currentTargetMRR: number;
  requiredForGoal: number;
  currentMRR_usd: number;
  currentARR_usd: number;
  currentTargetMRR_usd: number;
  requiredForGoal_usd: number;
  monthlyGrowth: number;
  pctOfTarget: number;
  pctThroughMonth: number;
  mrr: RegionArr;
  tgt: RegionArr;
  monthStart: RegionMap;
  regionCaptured: RegionMap;
  retool: RegionMap;
  proMrr: RegionMap;
  totalMRR: number[];
  fxNzd: RegionArr;
  fxUsd: RegionArr;
  pro: { companies: RegionMap; seats: RegionMap };
  plus: { companies: RegionMap; seats: RegionMap };
};
